import React, { useState, useEffect, useRef } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Button,
    Grid,
    TextField,
    CircularProgress,
    LinearProgress,
    Divider,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    Avatar,
    Chip,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LoginIcon from '@mui/icons-material/Login';
import InfoIcon from '@mui/icons-material/Info';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ScienceIcon from '@mui/icons-material/Science';
import FactoryIcon from '@mui/icons-material/Factory';
import ArticleIcon from '@mui/icons-material/Article';
import ChatIcon from '@mui/icons-material/Chat';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import config from './config';
import { countries } from './countries';
import { AGENT_NAME, AGENT_TITLE, DEFAULT_SUGGESTIONS, SALES_REP_ACTION, getBotReply, getTypingDelay } from './chatBot';
import './styles.css';
import { QrReader } from 'react-qr-reader';
import { Pie } from 'react-chartjs-2'; // Import the Pie chart component
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'; // Import Chart.js components

// Convert an HTML fragment from the WordPress API to plain text (strips tags, decodes entities)
const htmlToText = (html) =>
    (new DOMParser().parseFromString(html, 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

// Shared stage-gate catalog — used by both the K-LOG project checklist and
// the per-order "current gate" badges (OrdersPage / CartPage).
const PROJECT_GATES = [
    { id: 1, name: 'Project Charter Approval & Feasibility Sign-off' },
    { id: 2, name: 'Readiness to Procure & Modify' },
    { id: 3, name: 'Pre-Execution Stage-Gate ("Go/No-Go" for Manufacturing)' },
    {
        id: 4,
        name: 'Pre-Submission Gate',
        subItems: [
            { id: '4.1', name: 'Engineering / Trial Batches Executed' },
            { id: '4.2', name: 'Process Performance Qualification (PPQ) Protocol Approved' },
            { id: '4.3', name: 'PPQ Batches Completed' },
            { id: '4.4', name: 'Stability Studies Initiated' },
        ],
    },
    { id: 5, name: 'Project Closeout & Handover to Operations' },
];

// Deterministic mapping from an order's status string (WooCommerce live
// statuses, or the CartPage mock-order statuses) to its "current gate".
// This is a presentation-only heuristic — WooCommerce has no real gate data.
const GATE_ID_BY_ORDER_STATUS = {
    pending: 1,
    'on-hold': 2,
    processing: 3,
    completed: 5,
    cancelled: 1,
    refunded: 1,
    failed: 1,
    confirmed: 2,
    'in progress': 3,
    shipped: 4,
};

const getCurrentGateId = (status) => GATE_ID_BY_ORDER_STATUS[(status || '').toLowerCase()] ?? 1;

const darkTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#009FE3',
        },
        background: {
            default: '#ffffff',
            paper: '#ffffff',
        },
        appBar: {
            main: '#ffffff', // Couleur de l'AppBar
        }
    },
});

function App() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [activePage, setActivePage] = useState("news");
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [cart, setCart] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userName, setUserName] = useState('Demo');
    const [scannedProduct, setScannedProduct] = useState(null);
    const [scannedCodeContent, setScannedCodeContent] = useState(null);
    const [scannedCodeType, setScannedCodeType] = useState(null);
    const [chatMessages, setChatMessages] = useState([
        { sender: 'agent', text: `Hello, I'm ${AGENT_NAME} from ${AGENT_TITLE}. How can I help you today?`, time: new Date() },
    ]);
    const [chatInput, setChatInput] = useState('');
    const [agentTyping, setAgentTyping] = useState(false);
    const [chatSuggestions, setChatSuggestions] = useState(DEFAULT_SUGGESTIONS);
    const chatScrollRef = useRef(null);
    const [news, setNews] = useState([]);
    const [newsLoading, setNewsLoading] = useState(false);
    const [newsError, setNewsError] = useState(false);
    const [contactArea, setContactArea] = useState('');
    const [contactDrugProduct, setContactDrugProduct] = useState('');
    const [contactCountry, setContactCountry] = useState('');
    const [contactPopupOpen, setContactPopupOpen] = useState(false);

    const drugProductOptions = [
        'Solids',
        'Semi Solids',
        'Ophthalmic',
        'Inhalation',
        'Sterile Injectables (Ampoule)',
        'Sterile Injectables (Vials)',
        'Sterile Injectables (Prefilled Syringes)',
    ];

    const sendChatMessage = (presetText) => {
        const text = (typeof presetText === 'string' ? presetText : chatInput).trim();
        if (!text || agentTyping) return;
        if (text === SALES_REP_ACTION && isAuthenticated) {
            setActivePage('contact-sales');
            return;
        }
        setChatMessages((prev) => [...prev, { sender: 'user', text, time: new Date() }]);
        setChatInput('');
        setChatSuggestions([]);
        const displayName = userName.charAt(0).toUpperCase() + userName.slice(1);
        const reply = getBotReply(text, { isAuthenticated, userName: displayName });
        // Short pause before the agent starts "typing", like a real person reading the message
        setTimeout(() => setAgentTyping(true), 400);
        setTimeout(() => {
            setAgentTyping(false);
            setChatMessages((prev) => [...prev, { sender: 'agent', text: reply.text, time: new Date() }]);
            // The sales representatives page is only available once logged in
            setChatSuggestions(reply.suggestions.filter((s) => isAuthenticated || s !== SALES_REP_ACTION));
        }, 400 + getTypingDelay(reply.text));
    };

    // Keep the latest chat message in view
    useEffect(() => {
        if (chatScrollRef.current) {
            chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        }
    }, [chatMessages, agentTyping, activePage]);

    // Fetch the latest news from the official Siegfried website (WordPress REST API, CORS enabled)
    const fetchNews = async () => {
        setNewsLoading(true);
        setNewsError(false);
        try {
            const response = await axios.get('https://www.siegfried.ch/wp-json/wp/v2/posts', {
                params: {
                    per_page: 10,
                    _embed: 'wp:featuredmedia',
                    _fields: 'id,date,link,title,content,_links,_embedded',
                },
            });
            setNews(response.data.map((post) => {
                const media = post._embedded?.['wp:featuredmedia']?.[0];
                const summary = htmlToText(post.content?.rendered || '');
                return {
                    id: post.id,
                    date: new Date(post.date),
                    link: post.link,
                    title: htmlToText(post.title?.rendered || ''),
                    summary: summary.length > 160 ? `${summary.slice(0, 160).replace(/\s+\S*$/, '')}…` : summary,
                    image: media?.media_details?.sizes?.medium_large?.source_url || media?.source_url,
                };
            }));
        } catch (error) {
            console.error('Error fetching news:', error);
            setNewsError(true);
        } finally {
            setNewsLoading(false);
        }
    };

    const toggleDrawer = (open) => () => {
        setDrawerOpen(open);
    };

    // Function to fetch products from WooCommerce
    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.apiUrl}/wp-json/wc/v3/products`, {
                params: {
                    consumer_key: config.consumerKey,
                    consumer_secret: config.consumerSecret,
                },
            });
            setProducts(response.data);
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch orders for the client
    const fetchOrders = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${config.apiUrl}/wp-json/wc/v3/orders`, {
                params: {
                    consumer_key: config.consumerKey,
                    consumer_secret: config.consumerSecret,
                    email: 'demo@siegfried.ch',
                },
            });
            setOrders(response.data);
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch products or orders when the page changes
    useEffect(() => {
        if (activePage === "products") {
            fetchProducts();
        } else if (activePage === "orders") {
            fetchOrders();
        }
    }, [activePage]);

    // Load the news once, the first time the news page is shown
    useEffect(() => {
        if (activePage === 'news' && news.length === 0) {
            fetchNews();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePage]);

    // Utility function to format price and append "Euro"
    const formatPrice = (price) => {
        const numericPrice = parseFloat(price.replace(/[^\d.-]/g, ''));
        return `${numericPrice} Euro`;
    };

    const handleConnect = () => {
        // Simulate authentication logic
        if (email === 'haci' && password === '123') {
            toast.success('Connected successfully!', {
                position: 'bottom-right',
                autoClose: 3000,
            });
            setIsAuthenticated(true);
            setUserName(email);
            setDrawerOpen(false);
        } else {
            toast.error('Invalid username or password.', {
                position: 'bottom-right',
                autoClose: 3000,
            });
        }
    };

    const ConnectPage = () => (
        <div style={{ padding: 16 }}>
            <Typography variant="h4">Connect</Typography>
            <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />
            <Button
                variant="contained"
                color="primary"
                startIcon={<LoginIcon />}
                onClick={handleConnect}
                fullWidth
                style={{ marginTop: 16 }}
            >
                Connect
            </Button>
        </div>
    );


    // Function to generate chart data
    const getChartData = () => {
        const statuses = { pending: 0, processing: 0, completed: 0 };

        // Count the number of orders for each status
        orders.forEach((order) => {
            if (statuses[order.status] !== undefined) {
                statuses[order.status] += 1;
            }
        });

        return {
            labels: ['Pending', 'Processing', 'Completed'],
            datasets: [
                {
                    data: [statuses.pending, statuses.processing, statuses.completed],
                    backgroundColor: ['#FF6384', '#36A2EB', '#4BC0C0'],
                    hoverBackgroundColor: ['#FF6384', '#36A2EB', '#4BC0C0'],
                },
            ],
        };
    };

    const OrdersPage = () => (
        <div style={{ padding: 16 }}>
            <Typography variant="h4">Order Status</Typography>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                    <CircularProgress />
                </div>
            ) : (
                <Grid container spacing={3}>
                    {orders.map((order) => (
                        <Grid item xs={12} key={order.id}>
                            <div className="order-card">
                                <Typography variant="h6">Order #{order.id}</Typography>
                                <Typography variant="body2">{formatPrice(order.total)}</Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={order.status === 'completed' ? 100 : 50} // Example: 50% for "processing", 100% for "completed"
                                    style={{ marginTop: 10 }}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                                    <Typography variant="body2">
                                        Status: {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={`Gate ${getCurrentGateId(order.status)}`}
                                        title={PROJECT_GATES.find((g) => g.id === getCurrentGateId(order.status))?.name}
                                        style={{ backgroundColor: '#009FE3', color: '#fff' }}
                                    />
                                </div>
                            </div>
                        </Grid>
                    ))}
                </Grid>
            )}
        </div>
    );

    const AboutSiegfriedPage = () => {
        const keyFacts = [
            { label: 'Founded', value: '1873, in Zofingen, Switzerland' },
            { label: 'Headquarters', value: 'Zofingen, Switzerland' },
            { label: 'Global network', value: '16 sites across four continents' },
            { label: 'Employees', value: 'More than 3,800 worldwide' },
            { label: 'Customers', value: 'More than 500 globally' },
            { label: 'Listed on', value: 'SIX Swiss Exchange (SIX: SFZN)' },
        ];
        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">About Siegfried</Typography>
                <Typography variant="body2" style={{ marginTop: 8, fontStyle: 'italic', color: '#009FE3' }}>
                    "Taking innovations to industrial scale"
                </Typography>
                <img
                    src="/about/zofingen-headquarters.jpg"
                    alt="Siegfried headquarters in Zofingen, Switzerland"
                    style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8, marginTop: 16 }}
                />
                <Typography variant="body1" style={{ marginTop: 20 }}>
                    Founded in 1873 by pharmacist Samuel Benoni Siegfried in Zofingen, Switzerland, Siegfried has grown into a leading global Contract Development and Manufacturing Organization (CDMO) for the pharmaceutical industry.
                </Typography>
                <Typography variant="body1" style={{ marginTop: 16 }}>
                    Siegfried provides integrated services covering process development, optimization, regulatory registration, manufacturing, packaging and logistics — for both active pharmaceutical ingredients (APIs) and finished dosage forms.
                </Typography>
                <Typography variant="body1" style={{ marginTop: 16 }}>
                    Siegfried supplies around 200 of the 1,500 FDA-approved APIs and serves more than 500 customers worldwide, with its products reaching up to one billion people every year.
                </Typography>
                <div className="product-card" style={{ marginTop: 24 }}>
                    <Typography variant="h6" style={{ marginBottom: 8 }}>Key facts</Typography>
                    {keyFacts.map((fact, index) => (
                        <div
                            key={fact.label}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '6px 0',
                                borderBottom: index < keyFacts.length - 1 ? '1px solid #eee' : 'none',
                            }}
                        >
                            <Typography variant="body2" style={{ color: '#666' }}>{fact.label}</Typography>
                            <Typography variant="body2" style={{ textAlign: 'right', maxWidth: '60%' }}>{fact.value}</Typography>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const LatestNewsPage = () => (
        <div style={{ padding: 16 }}>
            <Typography variant="body1" style={{ marginTop: 20 }}>
                <center>
                <img style={{ width: '80%', maxWidth: 360 }} src="/logo-siegfried.svg" alt="Siegfried Logo" />
                <br/>
                </center>
            </Typography>
            <Typography variant="h4" style={{ marginTop: 24 }}>Latest News</Typography>
            <Typography variant="body2" style={{ color: '#666', marginTop: 4 }}>
                From siegfried.ch
            </Typography>
            {newsLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}>
                    <CircularProgress />
                </div>
            )}
            {!newsLoading && news.map((item) => (
                <div key={item.id} className="product-card" style={{ marginTop: 16 }}>
                    {item.image && (
                        <img
                            src={item.image}
                            alt=""
                            loading="lazy"
                            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 12 }}
                        />
                    )}
                    <Typography variant="caption" style={{ color: '#009FE3', fontWeight: 'bold' }}>
                        {item.date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                    <Typography variant="h6" style={{ marginTop: 4, lineHeight: 1.3 }}>{item.title}</Typography>
                    {item.summary && (
                        <Typography variant="body2" style={{ marginTop: 8, color: '#444' }}>{item.summary}</Typography>
                    )}
                    <Button
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        style={{ marginTop: 8, paddingLeft: 0 }}
                    >
                        Read more
                    </Button>
                </div>
            ))}
            {!newsLoading && news.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 40 }}>
                    <div style={{
                        width: 96,
                        height: 96,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 159, 227, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <ArticleIcon style={{ fontSize: 48, color: '#009FE3' }} />
                    </div>
                    <Typography variant="h6" style={{ marginTop: 16 }}>
                        {newsError ? 'News unavailable' : 'No news yet'}
                    </Typography>
                    <Typography variant="body2" style={{ marginTop: 8, color: '#666', textAlign: 'center' }}>
                        {newsError
                            ? 'We could not load the latest news from siegfried.ch. Please check your connection.'
                            : 'Check back soon for the latest updates and announcements from Siegfried.'}
                    </Typography>
                    <Button variant="outlined" color="primary" style={{ marginTop: 16 }} onClick={fetchNews}>
                        Retry
                    </Button>
                </div>
            )}
        </div>
    );

    const SiteList = ({ sites }) => (
        <>
            {sites.map((site) => (
                <div
                    key={site.name}
                    className="product-card"
                    style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 8 }}
                >
                    <LocationOnIcon style={{ color: '#009FE3', marginTop: 4 }} />
                    <div>
                        <Typography variant="h6">{site.name}</Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                            {site.location}
                        </Typography>
                        <Typography variant="body2" style={{ marginTop: 4 }}>
                            {site.description}
                        </Typography>
                        {site.specialization && (
                            <Typography variant="body2" style={{ marginTop: 8 }}>
                                <strong>Specialization:</strong> {site.specialization}
                            </Typography>
                        )}
                        {site.capacity && (
                            <Typography variant="body2" style={{ marginTop: 4 }}>
                                <strong>Capacity:</strong> {site.capacity}
                            </Typography>
                        )}
                        {site.certifications && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                                {site.certifications.map((cert) => (
                                    <Chip
                                        key={cert}
                                        size="small"
                                        label={cert}
                                        variant="outlined"
                                        style={{ borderColor: '#009FE3', color: '#009FE3' }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </>
    );

    const GateChecklist = ({ gates, completedIds }) => (
        <div>
            {gates.map((gate) => {
                const gateComplete = completedIds.includes(gate.id);
                return (
                    <div key={gate.id} style={{ marginBottom: 4 }}>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 0',
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FlagIcon style={{ color: '#009FE3' }} />
                                <Typography variant="body1">{`Gate ${gate.id}: ${gate.name}`}</Typography>
                            </div>
                            {gateComplete && <CheckCircleIcon style={{ color: '#4CAF50' }} />}
                        </div>
                        {gate.subItems && (
                            <div style={{ marginLeft: 40 }}>
                                {gate.subItems.map((sub) => {
                                    const subComplete = completedIds.includes(sub.id);
                                    return (
                                        <div
                                            key={sub.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '8px 0',
                                                borderBottom: '1px solid #f5f5f5',
                                            }}
                                        >
                                            <Typography variant="body2" style={{ color: '#555' }}>
                                                {sub.name}
                                            </Typography>
                                            {subComplete && (
                                                <CheckCircleIcon style={{ color: '#4CAF50' }} fontSize="small" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    const DrugSubstanceSitesPage = () => {
        const sites = [
            { name: 'Zofingen', location: 'Zofingen, Switzerland', description: 'Siegfried Group headquarters — development and commercial manufacturing of APIs and intermediates, including high-potency APIs.', certifications: ['GMP', 'FDA', 'Swissmedic'], capacity: 'Multi-purpose, commercial scale', specialization: 'High-potency APIs' },
            { name: 'Evionnaz', location: 'Evionnaz, Switzerland', description: 'API manufacturing site.', certifications: ['GMP', 'Swissmedic'], capacity: 'Multi-purpose, commercial scale', specialization: 'API manufacturing' },
            { name: 'Minden', location: 'Minden, Germany', description: 'Large-scale API facility with advanced process technology and high-containment capabilities.', certifications: ['GMP', 'FDA', 'EMA'], capacity: 'Large-scale, high-containment', specialization: 'High-containment APIs' },
            { name: 'St. Vulbas', location: 'Saint Vulbas, France', description: 'API manufacturing site.', certifications: ['GMP', 'EMA'], capacity: 'Multi-purpose, commercial scale', specialization: 'API manufacturing' },
            { name: 'Pennsville', location: 'Pennsville, New Jersey, USA', description: 'API manufacturing for the US market and global spray drying operations.', certifications: ['GMP', 'FDA'], capacity: 'Commercial scale with spray drying', specialization: 'Spray drying' },
            { name: 'Grafton', location: 'Grafton, Wisconsin, USA', description: 'Acceleration Hub for early-phase API development and manufacturing.', certifications: ['GMP', 'FDA'], capacity: 'Early-phase, pilot scale', specialization: 'Early-phase development' },
            { name: 'Athens', location: 'Athens, Georgia, USA', description: 'Early-phase API development and manufacturing.', certifications: ['GMP', 'FDA'], capacity: 'Early-phase, pilot scale', specialization: 'Early-phase development' },
            { name: 'Wilmington', location: 'Wilmington, Delaware, USA', description: 'Large-scale commercial API manufacturing.', certifications: ['GMP', 'FDA'], capacity: 'Large-scale, commercial', specialization: 'Commercial API manufacturing' },
            { name: 'Nantong', location: 'Nantong, China', description: 'API manufacturing site.', certifications: ['GMP', 'NMPA'], capacity: 'Multi-purpose, commercial scale', specialization: 'API manufacturing' },
            { name: 'Westbury', location: 'Westbury, Tasmania, Australia', description: 'Alkaloid extraction.', certifications: ['GMP', 'TGA'], capacity: 'Specialized extraction scale', specialization: 'Alkaloid extraction' },
        ];
        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">Drug Substance Sites</Typography>
                <SiteList sites={sites} />
            </div>
        );
    };

    const DrugProductSitesPage = () => {
        const sites = [
            { name: 'Hameln', location: 'Hameln, Germany', description: 'Sterile injectables — aseptic and terminally sterilized products in ampoules and vials.', certifications: ['GMP', 'FDA', 'EMA'], capacity: 'High-volume aseptic fill-finish', specialization: 'Sterile injectables' },
            { name: 'El Masnou', location: 'El Masnou, Spain', description: 'Ophthalmic products, with manufacturing expertise dating back to 1925.', certifications: ['GMP', 'EMA'], capacity: 'Commercial scale', specialization: 'Ophthalmic products' },
            { name: 'Barberà del Vallès', location: 'Barberà del Vallès, Spain', description: 'Solid dosage forms.', certifications: ['GMP', 'EMA'], capacity: 'Commercial scale', specialization: 'Solid dosage forms' },
            { name: 'Hal Far', location: 'Hal Far, Malta', description: 'Solid dosage forms.', certifications: ['GMP', 'EMA'], capacity: 'Commercial scale', specialization: 'Solid dosage forms' },
            { name: 'Irvine', location: 'Irvine, California, USA', description: 'Sterile injectables.', certifications: ['GMP', 'FDA'], capacity: 'Aseptic fill-finish', specialization: 'Sterile injectables' },
            { name: 'DINAMIQS', location: 'Zurich, Switzerland', description: 'Viral vectors for cell and gene therapies.', certifications: ['GMP', 'Swissmedic'], capacity: 'Specialized, cell & gene scale', specialization: 'Viral vector manufacturing' },
        ];
        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">Drug Product Sites</Typography>
                <SiteList sites={sites} />
            </div>
        );
    };

    const ProductsPage = () => {
        const handleQuantityChange = (productId, value) => {
            setQuantities({
                ...quantities,
                [productId]: value,
            });
        };

        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">Product List</Typography>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <Grid container spacing={3}>
                        {products.map((product) => (
                            <Grid item xs={12} sm={6} md={4} key={product.id}>
                                <div className="product-card">
                                    <img src={product.images[0]?.src} alt={product.name} className="product-image" />
                                    <Typography variant="h6" style={{ marginTop: '8px' }}>{product.name}</Typography>
                                    <Typography variant="body2">{formatPrice(product.price)}</Typography>

                                    <Grid container alignItems="center" justifyContent="space-between" style={{ marginTop: 10 }}>
                                        <Grid item>
                                            <TextField
                                                label="Quantity"
                                                type="number"
                                                InputProps={{ inputProps: { min: 1 } }}
                                                variant="outlined"
                                                size="small"
                                                defaultValue={1}
                                                onChange={(e) => handleQuantityChange(product.id, parseInt(e.target.value))}
                                                className="quantity-input"
                                                style={{ width: '100px' }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<AddShoppingCartIcon />}
                                                onClick={() => addToCart(product, quantities[product.id] || 1)}
                                                style={{ marginLeft: 10 }}
                                            >
                                                Add to Cart
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </div>
        );
    };

    const addToCart = (product, quantity) => {
        const newCartItem = { ...product, quantity };
        setCart([...cart, newCartItem]);
        toast.success(`${product.name} (Quantity: ${quantity}) added to cart!`, {
            position: 'bottom-right',
            autoClose: 3000,
        });
    };

    const CartPage = () => {
        const mockOrders = [
            { id: '123455', status: 'Confirmed', progress: 50, deliveryDate: '10.10.2026' },
            { id: '123456', status: 'In Progress', progress: 25, deliveryDate: '15.11.2026' },
            { id: '123457', status: 'Shipped', progress: 75, deliveryDate: '02.12.2026', trackingUrl: 'https://tracking.example.com/shipment/123457' },
            { id: '123458', status: 'Pending', progress: 10, deliveryDate: '20.01.2027' },
        ];

        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">My Orders</Typography>
                {mockOrders.map((order) => (
                    <div key={order.id} className="product-card" style={{ marginTop: 20 }}>
                        <Typography variant="h6">Order #{order.id}</Typography>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                            <Typography variant="body2">Status: {order.status}</Typography>
                            <Chip
                                size="small"
                                label={`Gate ${getCurrentGateId(order.status)}`}
                                title={PROJECT_GATES.find((g) => g.id === getCurrentGateId(order.status))?.name}
                                style={{ backgroundColor: '#009FE3', color: '#fff' }}
                            />
                        </div>
                        <LinearProgress variant="determinate" value={order.progress} style={{ marginTop: 10 }} />
                        <Typography variant="body2" style={{ marginTop: 10 }}>
                            Delivery date: {order.deliveryDate}
                        </Typography>
                        {order.trackingUrl && (
                            <a
                                href={order.trackingUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, color: '#009FE3', textDecoration: 'none' }}
                            >
                                <LocalShippingIcon fontSize="small" />
                                <Typography variant="body2" style={{ color: '#009FE3' }}>
                                    Track shipment
                                </Typography>
                            </a>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const removeFromCart = (index) => {
        const product = cart[index];
        const newCart = cart.filter((_, i) => i !== index);
        setCart(newCart);
        toast.info(`${product.name} removed from cart.`, {
            position: 'bottom-right',
            autoClose: 3000,
        });
    };

    const ProjectManagementPage = () => {
        const klogCompletedIds = [1, 2, 3, '4.1'];

    return (
        <div style={{ padding: 16 }}>
            <Typography variant="h4">Project: K-LOG</Typography>
            <div style={{ marginTop: 20 }}>
                <GateChecklist gates={PROJECT_GATES} completedIds={klogCompletedIds} />
            </div>
        </div>
    );
    };

    const formatChatTime = (date) => date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    const LiveChatPage = () => (
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 67px)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                <div style={{ position: 'relative' }}>
                    <Avatar style={{ backgroundColor: '#009FE3' }}>
                        <SupportAgentIcon />
                    </Avatar>
                    <span className="chat-online-dot" />
                </div>
                <div>
                    <Typography variant="subtitle1" style={{ fontWeight: 'bold', lineHeight: 1.2 }}>{AGENT_NAME}</Typography>
                    <Typography variant="caption" style={{ color: '#666' }}>
                        {agentTyping ? 'typing…' : `${AGENT_TITLE} · Online`}
                    </Typography>
                </div>
            </div>
            <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', marginTop: 16, marginBottom: 8 }}>
                {chatMessages.map((msg, index) => (
                    <div
                        key={index}
                        className="chat-message"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                            marginBottom: 10,
                        }}
                    >
                        <div
                            style={{
                                maxWidth: '80%',
                                padding: '8px 12px',
                                borderRadius: msg.sender === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                                backgroundColor: msg.sender === 'user' ? '#009FE3' : '#f0f0f0',
                                color: msg.sender === 'user' ? '#fff' : '#1a1a1a',
                            }}
                        >
                            <Typography variant="body2">{msg.text}</Typography>
                        </div>
                        <Typography variant="caption" style={{ color: '#999', marginTop: 2, fontSize: 11 }}>
                            {msg.sender === 'agent' ? `${AGENT_NAME} · ` : ''}{formatChatTime(msg.time)}
                        </Typography>
                    </div>
                ))}
                {agentTyping && (
                    <div className="chat-message" style={{ display: 'flex', marginBottom: 10 }}>
                        <div className="chat-typing" aria-label={`${AGENT_NAME} is typing`}>
                            <span /><span /><span />
                        </div>
                    </div>
                )}
            </div>
            {chatSuggestions.length > 0 && !agentTyping && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {chatSuggestions.map((suggestion) => (
                        <Chip
                            key={suggestion}
                            label={suggestion}
                            color="primary"
                            variant="outlined"
                            size="small"
                            onClick={() => sendChatMessage(suggestion)}
                        />
                    ))}
                </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
                <TextField
                    placeholder="Type your message..."
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') sendChatMessage(); }}
                />
                <Button variant="contained" color="primary" onClick={() => sendChatMessage()} disabled={agentTyping || !chatInput.trim()}>
                    Send
                </Button>
            </div>
        </div>
    );

    const ContactSalesPage = () => (
        <div style={{ padding: 16 }}>
            <Typography variant="h4">Contact Sales Representatives</Typography>
            <Typography variant="body2" style={{ color: '#666', marginTop: 8 }}>
                Tell us about your project and we'll connect you with the right sales representative.
            </Typography>
            <div className="product-card" style={{ marginTop: 24 }}>
                <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel id="contact-area-label">Area</InputLabel>
                    <Select
                        labelId="contact-area-label"
                        label="Area"
                        value={contactArea}
                        onChange={(e) => {
                            setContactArea(e.target.value);
                            setContactDrugProduct('');
                        }}
                    >
                        <MenuItem value="Drug Products">Drug Products</MenuItem>
                        <MenuItem value="Drug Substance">Drug Substance</MenuItem>
                    </Select>
                </FormControl>

                {contactArea === 'Drug Products' && (
                    <FormControl fullWidth variant="outlined" margin="normal">
                        <InputLabel id="contact-drug-product-label">Drug Product</InputLabel>
                        <Select
                            labelId="contact-drug-product-label"
                            label="Drug Product"
                            value={contactDrugProduct}
                            onChange={(e) => {
                                setContactDrugProduct(e.target.value);
                                setContactPopupOpen(true);
                            }}
                        >
                            {drugProductOptions.map((option) => (
                                <MenuItem key={option} value={option}>{option}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel id="contact-country-label">Country</InputLabel>
                    <Select
                        labelId="contact-country-label"
                        label="Country"
                        value={contactCountry}
                        onChange={(e) => {
                            setContactCountry(e.target.value);
                            if (contactArea === 'Drug Substance') {
                                setContactPopupOpen(true);
                            }
                        }}
                    >
                        <MenuItem value="International">International</MenuItem>
                        {countries.map((country) => (
                            <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        </div>
    );

    const ContactSalesDialog = () => (
        <Dialog open={contactPopupOpen} onClose={() => setContactPopupOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Sales Representative
                <IconButton size="small" onClick={() => setContactPopupOpen(false)}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 24 }}>
                <Avatar
                    src="/sales-reps/haci-h-polat.jpg"
                    alt="Haci H. Polat"
                    style={{ width: 96, height: 96 }}
                />
                <Typography variant="h6" style={{ marginTop: 16 }}>Haci H. Polat</Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
                    <EmailIcon fontSize="small" style={{ color: '#009FE3' }} />
                    <Typography variant="body2">email@siegfried.ch</Typography>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                    <PhoneIcon fontSize="small" style={{ color: '#009FE3' }} />
                    <Typography variant="body2">+41 00 000 00 00</Typography>
                </div>
            </DialogContent>
        </Dialog>
    );

    const ProductInfoPage = () => {
        const handleScan = (result, error) => {
            if (!!result) {
                setScannedCodeContent(result.text);
                setScannedCodeType(result.decodedText ? result.decodedText.type : 'Unknown Type');

                const randomProduct = products[Math.floor(Math.random() * products.length)];
                setScannedProduct(randomProduct ? randomProduct.name : 'Unknown Product');
            }

            if (!!error) {
                console.error('Error scanning QR code:', error);
            }
    };
        return (
            <div style={{ padding: 16 }}>
                <Typography variant="h4">Product Info</Typography>
                <QrReader
                    onResult={handleScan}
                    constraints={{ facingMode: 'environment' }}
                    style={{ width: '100%' }}
                />
                {scannedProduct && (
                    <div>
                        <Typography variant="h6" style={{ marginTop: 20 }}>
                            Scanned Product: {scannedProduct}
                        </Typography>
                        <Typography variant="body1" style={{ marginTop: 10 }}>
                            Barcode Content: {scannedCodeContent}
                        </Typography>
                        <Typography variant="body1" style={{ marginTop: 10 }}>
                            Barcode Type: {scannedCodeType}
                        </Typography>
                    </div>
                )}
            </div>
        );
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <div style={{ minHeight: '100vh' }}>
                <AppBar position="static" style={{ backgroundColor: darkTheme.palette.background.default, borderBottom: '3px solid #009FE3' }}>
                    <Toolbar>
                        <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleDrawer(true)}>
                            <MenuIcon />
                        </IconButton>
                        {/* Add the PNG icon between the menu and title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1, mr: 1 }}>
                            <img src="/icons/icon-192x192.png" alt="Logo" style={{ width: '30px', height: '30px' }} />
                        </Box>

                        <Typography variant="h6">Siegfried Mobile</Typography>
                    </Toolbar>
                </AppBar>

                <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)} style={{ backgroundColor: darkTheme.palette.background.paper }}>
                    <div style={{ width: 250, height: '100%' }}>
                        <div style={{ padding: 16, textAlign: 'center' }}>
                            <img src="/logo-siegfried.svg" alt="Siegfried Logo" style={{ height: 32 }} />
                        </div>
                        <div style={{ padding: 16, backgroundColor: '#009FE3' }}>
                            {isAuthenticated ? (
                                <Typography variant="body1" style={{ color: '#fff' }}>
                                    {userName}
                                </Typography>
                            ) : (
                                <>
                                    <TextField
                                        label="Username"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={{ mb: 1, backgroundColor: '#fff', borderRadius: 1 }}
                                    />
                                    <TextField
                                        label="Password"
                                        type="password"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        sx={{ mb: 1, backgroundColor: '#fff', borderRadius: 1 }}
                                    />
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleConnect}
                                        style={{ backgroundColor: '#fff', color: '#009FE3' }}
                                    >
                                        Login
                                    </Button>
                                </>
                            )}
                        </div>
                        <List>
                            <ListItem button onClick={() => { setActivePage('about-siegfried'); setDrawerOpen(false); }}>
                                <InfoIcon style={{ marginRight: 8 }} />
                                <ListItemText primary="About Siegfried" />
                            </ListItem>
                            <ListItem button onClick={() => { setActivePage('news'); setDrawerOpen(false); }}>
                                <ArticleIcon style={{ marginRight: 8 }} />
                                <ListItemText primary="Latest News" />
                            </ListItem>
                            {isAuthenticated && (
                                <>
                                    <ListItem button onClick={() => { setActivePage('drug-product-sites'); setDrawerOpen(false); }}>
                                        <FactoryIcon style={{ marginRight: 8 }} />
                                        <ListItemText primary="Drug Product Sites" />
                                    </ListItem>
                                    <ListItem button onClick={() => { setActivePage('drug-substance-sites'); setDrawerOpen(false); }}>
                                        <ScienceIcon style={{ marginRight: 8 }} />
                                        <ListItemText primary="Drug Substance Sites" />
                                    </ListItem>
                                    <Divider />
                                    <ListItem button onClick={() => { setActivePage('cart'); setDrawerOpen(false); }}>
                                        <ShoppingCartIcon style={{ marginRight: 8 }} />
                                        <ListItemText primary="My Orders" />
                                    </ListItem>
                                    <ListItem button onClick={() => { setActivePage('project-management'); setDrawerOpen(false); }}>
                                        <ListAltIcon style={{ marginRight: 8 }} />
                                        <ListItemText primary="My Projects" />
                                    </ListItem>
                                    <Divider />
                                </>
                            )}
                            <ListItem button onClick={() => { setActivePage('live-chat'); setDrawerOpen(false); }}>
                                <ChatIcon style={{ marginRight: 8 }} />
                                <ListItemText primary="Live Chat" />
                            </ListItem>
                            {isAuthenticated && (
                                <ListItem button onClick={() => { setActivePage('contact-sales'); setDrawerOpen(false); }}>
                                    <SupportAgentIcon style={{ marginRight: 8 }} />
                                    <ListItemText primary="Your sales representatives" />
                                </ListItem>
                            )}
                        </List>
                    </div>
                </Drawer>

                {activePage === 'about-siegfried' && <AboutSiegfriedPage />}
                {activePage === 'news' && LatestNewsPage()}
                {activePage === 'drug-substance-sites' && <DrugSubstanceSitesPage />}
                {activePage === 'drug-product-sites' && <DrugProductSitesPage />}
                {activePage === 'connect' && <ConnectPage />}
                {activePage === 'orders' && <OrdersPage />}
                {activePage === 'products' && <ProductsPage />}
                {activePage === 'cart' && <CartPage />}
                {activePage === 'product-info' && <ProductInfoPage />}
                {activePage === 'live-chat' && LiveChatPage()}
                {activePage === 'contact-sales' && <ContactSalesPage />}
                {activePage === 'project-management' && <ProjectManagementPage />}

                {/* Render other components based on activePage */}

                <ContactSalesDialog />
                <ToastContainer />
            </div>
        </ThemeProvider>
    );
}

export default App;
